import { type ThunkDispatch, createAsyncThunk } from "@reduxjs/toolkit"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config.ts"
import {
    CalculatorResultType,
    CoverageCheckResponseType,
} from "@common-shared/config/coverage.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { CHECKER_CONFIG } from "../../config/checker.config"
import { AmplitudeHelper } from "../../helpers/amplitude.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { CheckerService } from "../../services/checker.service"
import type { IDynamicCheckerFormInputData } from "../../types/data.types.ts"
import type { IThunkActionWithPracticeData } from "../../types/general.types.ts"
import {
    checkerReceivedContinuousMonitoringCoverageResult,
    checkerReceivedMedicaidCoverageResult,
    checkerSetContinuousMonitoringInputData,
    checkerSetCoverageChecksSubmitted,
    checkerSetMedicaidInputData,
} from "../slices/checker.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import type { RootState } from "../store"
import { userDetailsGetPracticeUsageQuota } from "./userDetails.thunks"

/**
 * Run bulk coverage checks for checker in GENERAL mode
 */
export const checkerRunGeneralChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunGeneralChecksPrefix", async (action, { dispatch, getState }) => {
    await runBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        getState() as RootState,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runGeneralCoverageCheck,
        dispatch,
        "checkerRunGeneralChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in SMART SCAN mode
 */
export const checkerRunSmartScanChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunSmartScanChecksPrefix", async (action, { dispatch, getState }) => {
    await runBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        getState() as RootState,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runSmartScanCoverageCheck,
        dispatch,
        "checkerRunSmartScanChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in DISCOVER mode
 */
export const checkerRunDiscoverChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunDiscoverChecksPrefix", async (action, { dispatch, getState }) => {
    await runBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        getState() as RootState,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runDiscoverCoverageCheck,
        dispatch,
        "checkerRunDiscoverChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in MEDICAID mode
 */
export const checkerRunMedicaidChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunMedicaidChecksPrefix", async (action, { dispatch, getState }) => {
    await runBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        getState() as RootState,
        inputData => {
            dispatch(checkerSetMedicaidInputData(inputData))
        },
        runMedicaidCoverageCheck,
        dispatch,
        "checkerRunMedicaidChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in AGENTIC PHONE mode
 */
export const checkerRunAgenticPhoneChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>(
    "checkerRunAgenticPhoneChecksPrefix",
    async (action, { dispatch, getState }) => {
        await runBulkCoverageChecks<IDynamicCheckerFormInputData>(
            action.payload,
            action.practice,
            getState() as RootState,
            inputData => {
                dispatch(checkerSetContinuousMonitoringInputData(inputData))
            },
            runAgenticPhoneCoverageCheck,
            dispatch,
            "checkerRunAgenticPhoneChecksLoading",
            action.onSuccess,
            action.onError,
            action.onFinally,
        )
    },
)

/**
 * Run smart scan check for checker -> RESTORE EXISTING coverage check
 */
export const checkerRestorePolicyThroughSmartScan = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        currentRequestId: string
        data: IDynamicCheckerFormInputData
    }>
>(
    "checkerRestorePolicyThroughSmartScanPrefix",
    async (action, { dispatch, getState }) => {
        dispatch(
            runtimeStartLoading("checkerRestorePolicyThroughSmartScanLoading"),
        )

        try {
            const result = await CheckerService.runSmartScanCoverageCheck(
                action.payload.data,
                action.practice,
            )

            if (result && !result.coverageResult.isError) {
                const rootState = getState() as RootState

                const checkResult: IOneVerifyCoverageCheckHistory = {
                    ...result,

                    coverageResult: {
                        ...result.coverageResult,

                        resultId: action.payload.currentRequestId,
                        isSmartScanResult: true,
                    },
                }

                switch (rootState.checker.manualCheckType) {
                    case QuotaUsageType.GENERAL:
                    case QuotaUsageType.DISCOVER:
                    case QuotaUsageType.SMART_SCAN:
                        dispatch(
                            checkerReceivedContinuousMonitoringCoverageResult(
                                checkResult,
                            ),
                        )

                        break
                }

                action.onSuccess?.(result)
            } else {
                ToastrHelper.warning(
                    "Unfortunately, Nirvana was not able to recover selected policy",
                )

                action.onError?.(result)
            }
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(
                runtimeStopLoading(
                    "checkerRestorePolicyThroughSmartScanLoading",
                ),
            )

            dispatch(
                userDetailsGetPracticeUsageQuota({
                    payload: action.practice,
                }),
            )

            action.onFinally?.()
        }
    },
)

/**
 * Common method for running checks in bulk
 */
const runBulkCoverageChecks = async <InputDataType extends { id?: string }>(
    data: InputDataType[],
    practice: IPractice,
    globalState: RootState,
    onSetInputDataInState: (data: InputDataType[]) => void,
    runSingleCheck: (
        data: InputDataType,
        practice: IPractice,
        dispatch: ThunkDispatch<unknown, unknown, any>,
        triesCount?: number,
    ) => Promise<void>,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    runtimeLoaderIdentifier: string,
    onSuccess?: (data?: any) => void,
    onError?: (data?: any) => void,
    onFinally?: () => void,
) => {
    dispatch(runtimeStartLoading(runtimeLoaderIdentifier))

    // IF it's internal user - we'll have extended batch size
    const batchSize = UtilHelper.isInternalUser(globalState.userDetails.profile)
        ? CHECKER_CONFIG.processBatchSizeExtended
        : CHECKER_CONFIG.processBatchSize

    try {
        // Sleep for X seconds here so breathing spinner stays longer during this process
        await UtilHelper.sleep(3)

        // Filter input data, it can have empty values
        // Need to copy these because by default they are immutable
        let inputData = JSON.parse(
            JSON.stringify(data.filter(item => !!item)),
        ) as InputDataType[]

        if (!inputData.length) {
            return
        }

        // Generate unique id for each of the input data array item
        // This id will be used for listening for results and mapping to needed input data
        try {
            inputData = inputData.map(item => {
                item.id = item.id || UtilHelper.generateUniqueVarchar(100)

                return item
            })
        } catch (e) {
            console.log(e)
        }

        onSetInputDataInState(inputData)
        dispatch(checkerSetCoverageChecksSubmitted(true))

        let processedCount = 0

        while (processedCount < inputData.length) {
            const coverageChecks = [] as Promise<any>[]

            // Generate processing promises with our "pagination' based on batch size
            for (let i = processedCount; i < processedCount + batchSize; i++) {
                if (!inputData[i]) {
                    break
                }

                coverageChecks.push(
                    runSingleCheck(inputData[i], practice, dispatch),
                )
            }

            processedCount += batchSize

            await Promise.all(coverageChecks)
        }

        if (data.length > 1) {
            AmplitudeHelper.logEvent(AmplitudeEvent.RAN_CHECK_BULK, {
                practiceId: practice.id,
                practiceName: practice.name,
                checks: data.length,
                runSuccess: true,
            })
        }

        onSuccess?.()
    } catch (e) {
        dispatch(checkerSetCoverageChecksSubmitted(false))

        if (data.length > 1) {
            AmplitudeHelper.logEvent(AmplitudeEvent.RAN_CHECK_BULK, {
                practiceId: practice.id,
                practiceName: practice.name,
                checks: data.length,
                runSuccess: false,
            })
        }

        onError?.()
    } finally {
        dispatch(runtimeStopLoading(runtimeLoaderIdentifier))

        dispatch(
            userDetailsGetPracticeUsageQuota({
                payload: practice,
            }),
        )

        onFinally?.()
    }
}

/**
 * Run single coverage check for GENERAL checker
 */
const runGeneralCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    triesCount = 1,
) => {
    if (!data.payer) {
        ToastrHelper.error("Payer is required for General check")

        return
    }

    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.COVERAGE,
            data.payer,
            data.memberId,
            data.inNetworkCheck,
        ),
        requestedHealthProvider: null,
        requestPayer: data.payer,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const needRetry = triesCount < CHECKER_CONFIG.maxGetEstimateRetry

            const result = await CheckerService.runGeneralCoverageCheck(
                data,
                practice,
                needRetry,
                networkType === "inNetworkCheck",
            )

            if (!result && needRetry) {
                await runGeneralCoverageCheck(
                    data,
                    practice,
                    dispatch,
                    triesCount + 1,
                )
                return
            }

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Run single coverage check for SMART SCAN checker
 */
const runSmartScanCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
) => {
    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.SCAN,
            null,
            data.memberId,
            data.inNetworkCheck,
            CalculatorResultType.memberNotFound,
            true,
        ),
        requestedHealthProvider: null,
        requestPayer: null,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const result = await CheckerService.runSmartScanCoverageCheck(
                data,
                practice,
                networkType === "inNetworkCheck",
                true,
            )

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Run single coverage check for DISCOVER checker
 */
const runDiscoverCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
) => {
    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.DISCOVER,
            null,
            data.memberId,
            data.inNetworkCheck,
            CalculatorResultType.memberNotFound,
        ),
        requestedHealthProvider: null,
        requestPayer: data.payer || null,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const result = await CheckerService.runDiscoverCoverageCheck(
                data,
                practice,
                networkType === "inNetworkCheck",
                true,
            )

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Run single coverage check for medicaid checker
 */
const runMedicaidCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    triesCount = 1,
) => {
    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.MEDICAID,
            null,
            undefined,
            true,
        ),
        requestedHealthProvider: null,
        requestPayer: null,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        const needRetry = triesCount < CHECKER_CONFIG.maxGetEstimateRetry

        const result = await CheckerService.runMedicaidCoverageCheck(
            data,
            practice,
            needRetry,
        )

        if (!result && needRetry) {
            await runMedicaidCoverageCheck(
                data,
                practice,
                dispatch,
                triesCount + 1,
            )
            return
        }

        dispatch(
            checkerReceivedMedicaidCoverageResult(result || defaultErrorResult),
        )
    } catch (e) {
        // If errored -> set default error result
        dispatch(checkerReceivedMedicaidCoverageResult(defaultErrorResult))
    }
}

/**
 * Run single coverage check for AGENTIC PHONE checker
 */
const runAgenticPhoneCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    triesCount = 1,
) => {
    if (!data.payer) {
        ToastrHelper.error("Payer is required for Agentic check")

        return
    }

    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            // Consider agentic all being COVERAGE type for now
            CoverageCheckResponseType.COVERAGE,
            data.payer,
            data.memberId,
            data.inNetworkCheck,
        ),
        requestedHealthProvider: null,
        requestPayer: data.payer,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const needRetry = triesCount < CHECKER_CONFIG.maxGetEstimateRetry

            const result = await CheckerService.runAgenticPhoneCoverageCheck(
                data,
                practice,
                needRetry,
                networkType === "inNetworkCheck",
            )

            if (!result && needRetry) {
                await runAgenticPhoneCoverageCheck(
                    data,
                    practice,
                    dispatch,
                    triesCount + 1,
                )
                return
            }

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}
